import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarCheck, ArrowRight, ShieldCheck } from "lucide-react";
import { Counter } from "@/components/landing/Counter";
import { useDemo } from "@/components/landing/DemoContext";

/* Purpose-built mobile hero — NOT a shrunk desktop.
   Cuts ruthlessly:
   - No "KAI is live" announcement strip (already shown on /kai)
   - No "The problem" red badge (the headline already says it)
   - No secondary "Try Clinic OS" CTA (scroll does that)
   - No 12-tool stack pill row (cost callout already proves the math)
   - Cost callout = one tight line, not a 2-row box
   - Single primary CTA + small reassurances + partner social proof */
export const MobileHero = ({ totalReplaced }) => {
  const openDemo = useDemo();

  return (
    <section
      data-testid="mobile-hero"
      id="top"
      className="relative overflow-hidden px-5 pt-16 pb-8 sm:hidden"
    >
      {/* Soft background tint */}
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[280px] bg-gradient-to-b from-rose-500/[0.05] via-violet-500/[0.03] to-transparent" />

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.05 }}
        className="font-display text-[2.05rem] font-black leading-[1.04] tracking-[-0.035em]"
      >
        You didn&apos;t open a clinic to{" "}
        <span className="bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
          manage 12 logins.
        </span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.12 }}
        className="mt-3 text-[14px] leading-relaxed text-muted-foreground"
      >
        One operating system replaces the dozen tools your front desk juggles. Less switching, more care.
      </motion.p>

      {/* Compressed cost line — single row, no box. Pure data-viz feel. */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.18 }}
        className="mt-5 flex items-baseline gap-2.5"
        data-testid="mobile-hero-cost-line"
      >
        <span className="font-display text-[1.65rem] font-black leading-none tracking-[-0.02em] text-rose-600 dark:text-rose-400">
          $<Counter value={totalReplaced} duration={1.5} />
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700/85 dark:text-rose-400/85">
          /mo you spend today
        </span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.24 }}
        className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold text-muted-foreground"
      >
        <span>12 logins</span>
        <span className="h-1 w-1 rounded-full bg-border" />
        <span>0 integration</span>
        <span className="h-1 w-1 rounded-full bg-border" />
        <span>0 ROI</span>
      </motion.div>

      {/* Single primary CTA */}
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        onClick={openDemo}
        data-testid="mobile-hero-book-demo"
        className="group mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-foreground text-[14px] font-bold text-background shadow-lg shadow-foreground/15 active:scale-[0.98]"
      >
        <CalendarCheck className="h-4 w-4" />
        Book a Demo
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </motion.button>

      {/* Reassurances — compact pill row */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.36 }}
        className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-muted-foreground"
        data-testid="mobile-hero-reassurances"
      >
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3 w-3 text-emerald-500" />
          From $297/mo
        </span>
        <span className="h-1 w-1 rounded-full bg-border" />
        <span>No credit card</span>
        <span className="h-1 w-1 rounded-full bg-border" />
        <span>Live in &lt; 14 days</span>
      </motion.div>

      {/* Partner social proof — kept tight */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.42 }}
        className="mt-5 flex items-center gap-2"
        data-testid="mobile-hero-partners"
      >
        <span className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Live with</span>
        {[
          { to: "/integrations/juvonno", logo: "juvonno.com", name: "Juvonno" },
          { to: "/integrations/jane-app", logo: "jane.app", name: "Jane App" },
        ].map((p) => (
          <Link
            key={p.name}
            to={p.to}
            data-testid={`mobile-hero-partner-${p.logo.split(".")[0]}`}
            className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2 py-1 transition-all active:scale-[0.97]"
          >
            <img loading="lazy" decoding="async"
              src={`https://www.google.com/s2/favicons?domain=${p.logo}&sz=128`}
              alt={`${p.name} logo`}
              className="h-3.5 w-3.5 rounded object-contain"
            />
            <span className="font-display text-[11px] font-bold tracking-tight text-foreground/85">{p.name}</span>
            <span className="h-1 w-1 rounded-full bg-emerald-500" />
          </Link>
        ))}
      </motion.div>
    </section>
  );
};
